import type * as CSS from 'csstype'
import { system, Config } from '@styled-system/core'
import { ResponsiveValue, TLength } from '../utils/types'

export interface BorderProps {
  /**
   * The border CSS property sets an element's border. It's a shorthand for border-width, border-style,
   * and border-color.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border)
   */
  border?: ResponsiveValue<CSS.BorderProperty<TLength>>
  /**
   * The border-width shorthand CSS property sets the width of all sides of an element's border.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-width)
   */
  borderWidth?: ResponsiveValue<CSS.BorderWidthProperty<TLength>>
  /**
   * The border-style shorthand CSS property sets the style of all sides of an element's border.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-style)
   */
  borderStyle?: ResponsiveValue<CSS.BorderStyleProperty>
  /**
   * The border-color shorthand CSS property sets the color of all sides of an element's border.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-color)
   */
  borderColor?: ResponsiveValue<CSS.BorderColorProperty>
  /**
   * The border-top CSS property is a shorthand that sets the values of border-top-width, border-top-style,
   * and border-top-color. These properties describe an element's top border.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top)
   */
  borderTop?: ResponsiveValue<CSS.BorderTopProperty<TLength>>
  /**
   * The border-right CSS property is a shorthand that sets border-right-width, border-right-style,
   * and border-right-color. These properties set an element's right border.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-right)
   */
  borderRight?: ResponsiveValue<CSS.BorderRightProperty<TLength>>
  /**
   * The border-bottom CSS property sets an element's bottom border. It's a shorthand for
   * border-bottom-width, border-bottom-style and border-bottom-color.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-bottom)
   */
  borderBottom?: ResponsiveValue<CSS.BorderBottomProperty<TLength>>
  /**
   * The border-left CSS property is a shorthand that sets the values of border-left-width,
   * border-left-style, and border-left-color. These properties describe an element's left border.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left)
   */
  borderLeft?: ResponsiveValue<CSS.BorderLeftProperty<TLength>>
  /**
   * The border-x proxy is a shorthand that sets the values of border-left-width,
   * border-left-style, border-left-color border-right-width, border-right-style,
   * and border-right-color. These properties describe an element's left and right borders.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-left)
   */
  borderX?: ResponsiveValue<CSS.BorderProperty<TLength>>
  /**
   * The border-top-color shorthand CSS property sets the color of the top side of an element's border.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-color)
   */
  borderTopColor?: ResponsiveValue<CSS.BorderTopColorProperty>
  /**
   * The border-right-color shorthand CSS property sets the color of right side of an element's border.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-color)
   */
  borderRightColor?: ResponsiveValue<CSS.BorderRightColorProperty>
  /**
   * The border-bottom-color shorthand CSS property sets the color of the bottom side of an element's border.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-color)
   */
  borderBottomColor?: ResponsiveValue<CSS.BorderBottomColorProperty>
  /**
   * The border-left-color shorthand CSS property sets the color of the left side of an element's border.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-color)
   */
  borderLeftColor?: ResponsiveValue<CSS.BorderLeftColorProperty>
  /**
   * The border-Y proxysets an element's top and bottom border. It's a shorthand for
   * border-bottom-width, border-bottom-style, border-bottom-color, border-top-width, border-top-style,
   * and border-top-color.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-top)
   */
  borderY?: ResponsiveValue<CSS.BorderProperty<TLength>>
  /**
   * The border-radius CSS property rounds the corners of an element's outer border edge. You can set a single
   * radius to make circular corners, or two radii to make elliptical corners.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-radius)
   */
  borderRadius?: ResponsiveValue<CSS.BorderRadiusProperty<TLength>>
  /**
   * The border-radius CSS property rounds the corners of an element's outer border edge. You can set a single
   * radius to make circular corners, or two radii to make elliptical corners.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-radius)
   */
  borderTopLeftRadius?: ResponsiveValue<
    CSS.BorderTopLeftRadiusProperty<TLength>
  >
  /**
   * The border-radius CSS property rounds the corners of an element's outer border edge. You can set a single
   * radius to make circular corners, or two radii to make elliptical corners.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-radius)
   */
  borderTopRightRadius?: ResponsiveValue<
    CSS.BorderTopRightRadiusProperty<TLength>
  >
  /**
   * The border-radius CSS property rounds the corners of an element's outer border edge. You can set a single
   * radius to make circular corners, or two radii to make elliptical corners.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-radius)
   */
  borderBottomLeftRadius?: ResponsiveValue<
    CSS.BorderBottomLeftRadiusProperty<TLength>
  >
  /**
   * The border-radius CSS property rounds the corners of an element's outer border edge. You can set a single
   * radius to make circular corners, or two radii to make elliptical corners.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/border-radius)
   */
  borderBottomRightRadius?: ResponsiveValue<
    CSS.BorderBottomRightRadiusProperty<TLength>
  >
  /**
   * The box-shadow CSS property adds shadow effects around an element's frame. You can set multiple effects
   * separated by commas. A box shadow is described by X and Y offsets relative to the element, blur and spread
   * radii, and color.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/box-shadow)
   */
  boxShadow?: ResponsiveValue<TLength>
}

const config: Config = {
  border: {
    property: 'border',
    scale: 'borders'
  },
  borderWidth: {
    property: 'borderWidth',
    scale: 'borderWidths'
  },
  borderStyle: {
    property: 'borderStyle',
    scale: 'borderStyles'
  },
  borderColor: {
    property: 'borderColor',
    scale: 'colors'
  },
  borderTop: {
    property: 'borderTop',
    scale: 'borders'
  },
  borderRight: {
    property: 'borderRight',
    scale: 'borders'
  },
  borderBottom: {
    property: 'borderBottom',
    scale: 'borders'
  },
  borderLeft: {
    property: 'borderLeft',
    scale: 'borders'
  },
  borderTopColor: {
    property: 'borderTopColor',
    scale: 'colors'
  },
  borderRightColor: {
    property: 'borderRightColor',
    scale: 'colors'
  },
  borderBottomColor: {
    property: 'borderBottomColor',
    scale: 'colors'
  },
  borderLeftColor: {
    property: 'borderLeftColor',
    scale: 'colors'
  },
  borderX: {
    properties: ['borderLeft', 'borderRight'],
    scale: 'borders'
  },
  borderY: {
    properties: ['borderTop', 'borderBottom'],
    scale: 'borders'
  },
  borderRadius: {
    property: 'borderRadius',
    scale: 'radii'
  },
  borderTopLeftRadius: {
    property: 'borderTopLeftRadius',
    scale: 'radii'
  },
  borderTopRightRadius: {
    property: 'borderTopRightRadius',
    scale: 'radii'
  },
  borderBottomLeftRadius: {
    property: 'borderBottomLeftRadius',
    scale: 'radii'
  },
  borderBottomRightRadius: {
    property: 'borderBottomRightRadius',
    scale: 'radii'
  },
  boxShadow: {
    property: 'boxShadow',
    scale: 'shadows'
  }
}

export const borderProps = system(config)
